import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { TableModel } from '../../../../components/table';
import { reuseRows } from '../../../../components/rows';
import { csvBlob, csvDecimal, formatCount, formatDate, formatDecimal, formatPercent } from '../../../../domain';
import { FileSaver } from '../../../../infra';
import { CollaboratorsKeys } from '../collaborators/collaborators.key';
import { CompanyApi } from '../companies/companies.api';
import { IcpApi } from './icp.api';
import { ICP_DEFAULT_SORT, IcpKeys } from './icp.key';
import { ICP_COLUMNS, ICP_WINDOWS, NO_SIZE_BAND } from './icp.types';
import { IcpColumnScope, IcpCompanyRowScope, IcpGroupRowScope, IcpHighlightScope, IcpKpiScope, IcpOptionScope, IcpPersonaCellScope, IcpPersonaRowScope, IcpPersonaTableScope, IcpScope } from './icp.scope';
const LOG = Logger.get('Mormaii.IcpPresenter');
// @Inject
const api = IcpApi.INSTANCE;
const companyApi = CompanyApi.INSTANCE;
/** A fracao que a API manda (0 a 1) como a tela a escreve: `42,5%`, ou `-` sem valor. */
function percentOf(fraction) {
    return fraction === null || fraction === undefined ? '-' : formatPercent(fraction * 100);
}
/** O score de 0 a 100, com uma casa: `72,4`. */
function scoreOf(value) {
    return value === null || value === undefined ? '-' : formatDecimal(value);
}
/**
 * O nome de cada faixa na tela, no vocabulario do design do cliente (Excelente, Bom,
 * Atencao - `demandas/design/`, decidido em 16/10/2026). O servidor manda o rotulo dele
 * (`IcpBand.java`, "Ideal" e "Em risco"), mas quem escreve o texto de tela e o painel.
 */
export const BAND_LABELS = {
    IDEAL: 'Excelente',
    BOM: 'Bom',
    EM_RISCO: 'Atenção',
    AMOSTRA_INSUFICIENTE: 'Amostra insuficiente',
    SEM_USO: 'Sem uso'
};
/** A ordem das faixas na coluna: da melhor para a pior, com as sem score no fim. */
const BAND_ORDER = {
    IDEAL: 0,
    BOM: 1,
    EM_RISCO: 2,
    AMOSTRA_INSUFICIENTE: 3,
    SEM_USO: 4
};
/** O valor de cada coluna ordenavel. O porte ordena pelo numero que o define. */
const SORT_VALUE = {
    name: (company) => company.name,
    score: (company) => company.score,
    band: (company) => BAND_ORDER[company.band],
    segment: (company) => company.segmentLabel,
    size: (company) => company.contractedHeadcount ?? company.collaborators,
    collaborators: (company) => company.collaborators,
    activation: (company) => company.activation,
    engagement: (company) => company.engagement,
    retention: (company) => company.retention,
    training: (company) => company.training
};
function scorecardTableModel(account) {
    const model = new TableModel({
        name: 'icp',
        account,
        legacy: { widths: 'mormaii.columnWidths:icp' }
    });
    model.setColumns(ICP_COLUMNS.map((column) => ({ field: column.field, sortValue: SORT_VALUE[column.field] })));
    model.setVisible(ICP_COLUMNS.map((column) => column.field));
    return model;
}
/** A coluna da empresa - a primeira - fica; as outras se arrastam. */
const FIXED_COLUMNS = { start: 1 };
/** Colunas de texto comecam em ordem alfabetica; as de numero, do maior para o menor. */
const TEXT_COLUMNS = ['name', 'segment'];
/**
 * O Perfil de Cliente Ideal: quais empresas-cliente tiram valor da plataforma, e o que
 * elas tem em comum. So o administrador geral entra.
 *
 * Duas abas. "Empresas" sao duas perguntas ao servidor, em PARALELO - a visao geral e o
 * scorecard; "Persona" e uma terceira, feita so quando a aba abre, e so com a janela: a
 * persona compara pessoas, e segmento e porte sao recortes de empresa.
 *
 * O scorecard chega inteiro e e ordenado e paginado aqui: a ordem e a pagina estao no
 * endereco, e trocar uma delas nao volta ao servidor.
 */
export class IcpPresenter extends CubePresenter {
    constructor(app) {
        super(app, new IcpScope());
        /**
         * O modelo do scorecard: a largura de cada coluna e a ordem das linhas. Sem valor vai
         * sempre para o fim, nas duas direcoes - uma empresa sem score nao e a pior. A largura
         * escolhida antes dele, na chave do antigo `ColumnWidths`, e lida na primeira vez.
         */
        this.table = scorecardTableModel(() => this.app.currentSession?.id);
        this.parentSlot = NOOP_VOID;
        this.at = {
            windowDays: 30,
            tab: 'companies',
            onlyUnsegmented: false,
            sort: ICP_DEFAULT_SORT,
            page: 1
        };
        /** O catalogo de segmentos, buscado uma vez por visita. */
        this.catalog = [];
        /**
         * As faixas de porte do filtro. Vem da API, e so a resposta SEM filtro de porte traz
         * todas - com um porte escolhido, a comparacao por porte so tem aquele.
         */
        this.sizeBands = [];
        this.pending = 0;
        this.pendingPersona = 0;
    }
    async applyParameters(intent, initialization, last) {
        const keys = new IcpKeys(this.app, intent);
        // A guarda de papel, antes de qualquer rede: recusada, a tela nem chega a montar.
        if (!(await keys.owner?.allowEntry('icp', intent))) {
            return false;
        }
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onClearSegment = this.action(this.onClearSegment);
            this.scope.onClearSize = this.action(this.onClearSize);
            this.scope.onRefresh = this.action(this.onRefresh);
            this.scope.onToggleUnsegmented = this.action(this.onToggleUnsegmented);
            this.scope.onExportScorecard = this.action(this.onExportScorecard);
            this.scope.onPreviousPage = this.action(this.onPreviousPage);
            this.scope.onNextPage = this.action(this.onNextPage);
            this.scope.onGoToPage = this.action(this.onGoToPage);
            await this.loadCatalog();
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        if (last) {
            this.owner?.showSection('icp', 'Perfil de Cliente Ideal (ICP)', 'Quais empresas-cliente tiram valor da plataforma e o que elas têm em comum');
        }
        this.parentSlot(this.scope);
        this.scope.layout = this.table.show(this);
        await this.moveTo({
            windowDays: keys.windowDays,
            segmentId: keys.segmentId,
            sizeBand: keys.sizeBand,
            tab: keys.tab,
            onlyUnsegmented: keys.onlyUnsegmented,
            sort: keys.sort,
            page: keys.page
        });
        return true;
    }
    publishParameters(intent) {
        const keys = new IcpKeys(this.app, intent);
        keys.windowDays = this.at.windowDays;
        keys.segmentId = this.at.segmentId;
        keys.sizeBand = this.at.sizeBand;
        keys.tab = this.at.tab;
        keys.onlyUnsegmented = this.at.onlyUnsegmented;
        keys.sort = this.at.sort;
        keys.page = this.at.page;
    }
    async go(change) {
        await this.moveTo({ ...this.at, ...change });
        this.updateHistory();
    }
    async moveTo(target) {
        this.showRequest(target);
        const filter = {
            windowDays: target.windowDays,
            segmentId: target.segmentId,
            sizeBand: target.sizeBand
        };
        // Ordem, pagina, aba e o atalho de "sem segmento" nao voltam ao servidor: o que
        // esta em maos responde.
        const signature = JSON.stringify(filter);
        if (signature !== this.lastQuery) {
            const token = ++this.pending;
            this.scope.loading = true;
            this.scope.error = undefined;
            try {
                const [overview, companies] = await Promise.all([api.fetchOverview(filter), api.fetchCompanies(filter)]);
                if (token !== this.pending) {
                    return;
                }
                this.lastQuery = signature;
                this.overview = overview;
                this.companies = companies;
                if (target.sizeBand === undefined) {
                    this.sizeBands = (overview.bySize ?? [])
                        .filter((group) => group.key !== NO_SIZE_BAND)
                        .map((group) => ({ key: group.key, label: group.label }));
                }
            }
            catch (caught) {
                if (token !== this.pending) {
                    return;
                }
                this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar o ICP.';
                // O recorte que vale continua o anterior, com os numeros dele.
                this.showRequest(this.at);
                return;
            }
            finally {
                if (token === this.pending) {
                    this.scope.loading = false;
                }
            }
        }
        this.at = target;
        this.showRequest(target);
        this.showOverview();
        this.showScorecard();
        if (target.tab === 'persona') {
            await this.loadPersona(target.windowDays);
        }
    }
    // ========== OS FILTROS ==========
    showRequest(target) {
        this.scope.tab = target.tab;
        this.scope.windows = ICP_WINDOWS.map((days) => this.option(`${days} dias`, String(days), days === target.windowDays, () => this.go({ windowDays: days, page: 1 })));
        this.scope.segments = this.catalog.map((segment) => this.option(segment.label, String(segment.id), segment.id === target.segmentId, () => this.go({ segmentId: segment.id, page: 1 })));
        this.scope.segmentSummary = this.catalog.find((segment) => segment.id === target.segmentId)?.label ?? '';
        this.scope.sizes = this.sizeBands.map((band) => this.option(band.label, band.key, band.key === target.sizeBand, () => this.go({ sizeBand: band.key, page: 1 })));
        this.scope.sizeSummary = this.sizeBands.find((band) => band.key === target.sizeBand)?.label ?? '';
        this.scope.tabs = [
            this.option('Empresas', 'companies', target.tab === 'companies', () => this.go({ tab: 'companies' })),
            this.option('Persona', 'persona', target.tab === 'persona', () => this.go({ tab: 'persona' }))
        ];
    }
    option(label, value, current, select) {
        const scope = new IcpOptionScope();
        scope.identity = value;
        scope.label = label;
        scope.value = value;
        scope.current = current;
        scope.onSelect = this.action(select);
        scope.update = this.update;
        return scope;
    }
    /**
     * O catalogo de segmentos do filtro.
     *
     * Opcional: sem ele o ICP abre do mesmo jeito, so sem o filtro de segmento - derrubar a
     * tela por causa do catalogo seria desproporcional.
     */
    async loadCatalog() {
        try {
            this.catalog = await companyApi.fetchSegments();
        }
        catch (caught) {
            LOG.warn('Could not load the company segments', caught);
        }
    }
    // ========== EMPRESAS ==========
    showOverview() {
        const overview = this.overview;
        this.scope.hasOverview = !!overview;
        if (!overview) {
            return;
        }
        const kpis = overview.kpis;
        const days = overview.windowDays;
        const method = overview.methodology;
        this.scope.kpis = [
            this.kpi({
                label: 'Empresas com uso',
                value: `${formatCount(kpis.companiesWithUse)} de ${formatCount(kpis.companies)}`,
                hint: `${formatCount(kpis.companiesWithScore)} com amostra para score`,
                help: 'Empresas ativas com pelo menos um colaborador que já sincronizou dado.',
                icon: 'company'
            }),
            this.kpi({
                label: 'Com atenção',
                value: formatCount(kpis.companiesAtRisk),
                hint: 'score abaixo da faixa Bom',
                help: 'Empresas com amostra suficiente e score abaixo do limiar da faixa Bom.',
                icon: 'alert'
            }),
            this.kpi({
                label: 'Sem uso',
                value: formatCount(kpis.companiesWithoutUse),
                hint: 'ninguém sincronizou',
                help: 'Empresas ativas sem nenhum colaborador que já sincronizou dado.',
                icon: 'users'
            }),
            // A janela vai na linha de baixo: "Engajamento em 30 dias" nao cabe no rotulo do
            // cartao com cinco lado a lado, e cortado ele perdia justamente o numero de dias.
            this.kpi({
                label: 'Engajamento',
                value: percentOf(kpis.engagementB2b),
                hint: `B2B em ${days} dias · sem empresa: ${percentOf(kpis.engagementB2c)}`,
                help: `Colaboradores com medição ou treino nos últimos ${days} dias, sobre a base. B2B soma ` +
                    `${formatCount(kpis.collaboratorsB2b)} colaboradores; sem empresa, ${formatCount(kpis.usersB2c)} usuários.`,
                icon: 'activity'
            }),
            this.kpi({
                label: 'Retenção B2B',
                value: percentOf(kpis.retentionB2b),
                hint: `de quem começou há ${method.retentionStartedDays} dias ou mais`,
                help: `Dos colaboradores que começaram a sincronizar há pelo menos ${method.retentionStartedDays} dias, ` +
                    `quantos têm dado nos últimos ${method.retentionRecentDays}.`,
                icon: 'repeat'
            })
        ];
        this.showIdealProfile(overview);
        this.scope.bySegment = (overview.bySegment ?? []).map((group) => this.groupRow(group));
        this.scope.bySize = (overview.bySize ?? []).map((group) => this.groupRow(group));
        // Os pesos e limiares vem da configuracao do servidor, e nao de texto fixo aqui.
        const weight = (value) => `${Math.round(value * 100)}%`;
        this.scope.methodologyTitle = `Como o score é calculado (dados até ${formatDate(overview.endDate)})`;
        this.scope.methodology = [
            `Score de 0 a 100 = ${weight(method.weightEngagement)} engajamento + ${weight(method.weightRetention)} retenção + ` +
                `${weight(method.weightTraining)} treino + ${weight(method.weightActivation)} ativação. Métrica que não se ` +
                'aplica sai da conta, e os pesos das outras se redistribuem.',
            'Ativação: colaboradores que já sincronizaram alguma vez. Engajamento: com medição ou treino nos últimos ' +
                `${method.windowDays} dias. Retenção: de quem começou há ${method.retentionStartedDays} dias ou mais, ` +
                `quantos têm dado nos últimos ${method.retentionRecentDays}. Treino: ativos com pelo menos um treino na janela.`,
            `Faixas: Excelente a partir de ${method.idealMinScore}, Bom a partir de ${method.goodMinScore} e Atenção abaixo ` +
                `disso. Com menos de ${method.minSample} colaboradores que já sincronizaram, a empresa fica em Amostra insuficiente.`,
            'A base é quem tem cadastro ativo e ficha de paciente, pela empresa do paciente, com a mesma regra de "ativo" ' +
                'de Adoção e Engajamento. Saúde e pressão arterial ficam fora do score.'
        ];
    }
    /** O "Perfil ideal hoje": o que mais sobra nas empresas Ideal em relacao as demais com score. */
    showIdealProfile(overview) {
        const profile = overview.idealProfile;
        const highlighted = (profile.dimensions ?? []).filter((dimension) => dimension.highlightKey !== null);
        this.scope.idealSummary =
            `${formatCount(profile.idealCompanies)} empresa(s) Excelente comparada(s) com ` +
                `${formatCount(profile.otherCompaniesWithScore)} outra(s) com score`;
        if (profile.idealCompanies === 0) {
            this.scope.idealEmpty =
                'Nenhuma empresa está na faixa Excelente neste recorte, então ainda não há perfil para descrever.';
        }
        else if (highlighted.length === 0) {
            this.scope.idealEmpty = 'As empresas Excelente não se distinguem das demais em nenhuma dimensão.';
        }
        else {
            this.scope.idealEmpty = undefined;
        }
        this.scope.highlights =
            profile.idealCompanies === 0
                ? []
                : highlighted.map((dimension) => {
                    const shareOf = (side) => dimension[side].find((share) => share.key === dimension.highlightKey)?.share ?? null;
                    const scope = new IcpHighlightScope();
                    scope.identity = dimension.key;
                    scope.dimension = dimension.label;
                    scope.value = dimension.highlightLabel ?? '';
                    scope.detail =
                        `${percentOf(shareOf('ideal'))} nas Excelente contra ${percentOf(shareOf('others'))} nas demais ` +
                            (dimension.unit === 'COMPANIES' ? '(em empresas)' : '(em colaboradores)');
                    scope.update = this.update;
                    return scope;
                });
        this.scope.smallSample = profile.smallSample;
    }
    groupRow(group) {
        const scope = new IcpGroupRowScope();
        scope.identity = group.key;
        scope.label = group.label;
        scope.companiesLabel = formatCount(group.companies);
        scope.withScoreLabel = formatCount(group.companiesWithScore);
        scope.averageScoreLabel = scoreOf(group.averageScore);
        scope.engagementLabel = percentOf(group.engagement);
        scope.retentionLabel = percentOf(group.retention);
        scope.update = this.update;
        return scope;
    }
    kpi(spec) {
        const scope = new IcpKpiScope();
        scope.identity = spec.label;
        scope.label = spec.label;
        scope.value = spec.value;
        scope.hint = spec.hint;
        scope.help = spec.help;
        scope.icon = spec.icon;
        scope.update = this.update;
        return scope;
    }
    // ========== O SCORECARD ==========
    /**
     * As colunas do scorecard, na ordem que a pessoa arrastou. A da empresa identifica a
     * linha e nao sai do lugar. A ordem e preferencia de quem le: nao vai para o endereco.
     */
    buildColumns() {
        const descending = this.at.sort.startsWith('-');
        const sortedField = descending ? this.at.sort.slice(1) : this.at.sort;
        const all = ICP_COLUMNS.map((column) => column.field);
        const order = this.table.arrange(all, FIXED_COLUMNS);
        // A coluna que ja esta na tela e o mesmo scope, atualizado - ver `reuseRows`.
        this.scope.columns = reuseRows(this.scope.columns, order, (name) => name, (name, reused) => {
            const column = ICP_COLUMNS.find((item) => item.field === name);
            const scope = reused ?? new IcpColumnScope();
            scope.identity = column.field;
            scope.field = column.field;
            scope.label = column.label;
            scope.sorted = column.field === sortedField;
            scope.descending = scope.sorted && descending;
            scope.movable = this.table.isMovable(all, column.field, FIXED_COLUMNS);
            scope.onSort = this.action(this.onSort.bind(this, column.field));
            scope.onDrop = (source) => {
                if (this.table.move(all, source, column.field, FIXED_COLUMNS)) {
                    this.buildColumns();
                }
            };
            scope.update = this.update;
            return scope;
        });
        this.table.setVisible(order);
    }
    /** As empresas do scorecard, na ordem pedida - todas as paginas. Tambem e o que o CSV leva. */
    visibleCompanies() {
        const all = this.companies?.companies ?? [];
        const visible = this.scope.onlyUnsegmented ? all.filter((company) => company.segmentId === null) : all;
        this.table.setSort(this.at.sort);
        return this.table.sortRows(visible);
    }
    showScorecard() {
        const answer = this.companies;
        this.scope.hasScorecard = !!answer;
        if (!answer) {
            return;
        }
        const unsegmented = (answer.companies ?? []).filter((company) => company.segmentId === null).length;
        this.scope.unsegmentedCount = unsegmented;
        // Sem empresa a classificar o atalho some - e o filtro dele nao pode esconder a tabela.
        this.scope.onlyUnsegmented = this.at.onlyUnsegmented && unsegmented > 0;
        this.scope.scorecardDescription = `Score de 0 a 100 na janela de ${answer.windowDays} dias. Clique no nome para ver os colaboradores da empresa.`;
        const sorted = this.visibleCompanies();
        this.scope.total = sorted.length;
        // A pagina do endereco pode nao existir mais: o recorte novo encolheu a lista.
        const page = Math.min(this.at.page, this.scope.pageCount);
        this.at = { ...this.at, page };
        this.scope.page = page;
        this.buildColumns();
        const start = (page - 1) * this.scope.perPage;
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        this.scope.rows = reuseRows(this.scope.rows, sorted.slice(start, start + this.scope.perPage), (company) => company.companyId, (company, reuse) => this.buildRow(company, reuse));
    }
    buildRow(company, reuse) {
        const scope = reuse ?? new IcpCompanyRowScope();
        scope.identity = company.companyId;
        scope.companyId = company.companyId;
        scope.name = company.name;
        scope.scoreLabel = scoreOf(company.score);
        scope.scoreMuted = company.band === 'AMOSTRA_INSUFICIENTE';
        scope.band = company.band;
        scope.bandLabel = BAND_LABELS[company.band] ?? company.bandLabel;
        scope.segmentLabel = company.segmentLabel ?? '';
        scope.sizeLabel = company.sizeBandLabel ?? '';
        scope.sizeEstimated = company.sizeSource === 'CADASTRADO';
        scope.sizeHint =
            company.sizeSource === 'CONTRATADO'
                ? 'Colaboradores contratados'
                : 'Sem contrato: colaboradores cadastrados';
        scope.collaboratorsLabel = formatCount(company.collaborators);
        scope.activationLabel = percentOf(company.activation);
        scope.engagementLabel = percentOf(company.engagement);
        scope.retentionLabel = percentOf(company.retention);
        scope.trainingLabel = percentOf(company.training);
        scope.onOpenCollaborators = this.action(this.onOpenCollaborators.bind(this, company.companyId));
        scope.update = this.update;
        return scope;
    }
    // ========== A PERSONA ==========
    async loadPersona(windowDays) {
        if (this.workforce?.windowDays === windowDays) {
            this.showPersona();
            return;
        }
        const token = ++this.pendingPersona;
        this.scope.personaLoading = true;
        this.scope.personaTables = [];
        try {
            const workforce = await api.fetchWorkforce(windowDays);
            if (token !== this.pendingPersona) {
                return;
            }
            this.workforce = workforce;
            this.showPersona();
        }
        catch (caught) {
            if (token !== this.pendingPersona) {
                return;
            }
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar a persona.';
        }
        finally {
            if (token === this.pendingPersona) {
                this.scope.personaLoading = false;
            }
        }
    }
    showPersona() {
        const workforce = this.workforce;
        if (!workforce) {
            return;
        }
        this.scope.personaDescription =
            `Engajamento em ${workforce.windowDays} dias por perfil, com as empresas (B2B) e os usuários sem empresa ` +
                'lado a lado. Serve para decisão de produto e de venda, não para segmentar pessoas.';
        this.scope.personaFootnote =
            `Grupos com menos de ${workforce.minCell} pessoas aparecem como "menos de ${workforce.minCell}" e ficam ` +
                'fora dos percentuais: dado de saúde é sensível na LGPD.';
        this.scope.personaTables = (workforce.dimensions ?? []).map((dimension) => {
            // Os valores seguem a ordem do servidor; um valor pode existir so de um dos lados.
            const values = [];
            for (const value of [...dimension.b2b, ...dimension.b2c]) {
                if (!values.some((known) => known.key === value.key)) {
                    values.push({ key: value.key, label: value.label });
                }
            }
            const table = new IcpPersonaTableScope();
            table.identity = dimension.key;
            table.label = dimension.label;
            table.rows = values.map(({ key, label }) => {
                const row = new IcpPersonaRowScope();
                row.identity = key;
                row.label = label;
                row.b2b = this.personaCell(dimension.b2b.find((value) => value.key === key), workforce.minCell);
                row.b2c = this.personaCell(dimension.b2c.find((value) => value.key === key), workforce.minCell);
                row.update = this.update;
                return row;
            });
            table.update = this.update;
            return table;
        });
    }
    personaCell(value, minCell) {
        const cell = new IcpPersonaCellScope();
        cell.absent = !value;
        cell.suppressed = !!value?.suppressed;
        cell.peopleLabel = value?.suppressed ? `menos de ${minCell}` : formatCount(value?.people);
        cell.shareLabel = percentOf(value?.share);
        cell.engagementLabel = percentOf(value?.engagement);
        cell.update = this.update;
        return cell;
    }
    // ========== ACOES ==========
    async onClearSegment() {
        await this.go({ segmentId: undefined, page: 1 });
    }
    async onClearSize() {
        await this.go({ sizeBand: undefined, page: 1 });
    }
    /** O "Atualizar": refaz as consultas sem mudar filtro algum. */
    async onRefresh() {
        this.lastQuery = undefined;
        this.workforce = undefined;
        await this.moveTo(this.at);
    }
    async onToggleUnsegmented() {
        await this.go({ onlyUnsegmented: !this.scope.onlyUnsegmented, page: 1 });
    }
    /** O primeiro clique numa coluna usa a ordem natural dela; o segundo inverte. */
    async onSort(field) {
        const descending = this.at.sort === field ? true : this.at.sort === `-${field}` ? false : !TEXT_COLUMNS.includes(field);
        await this.go({ sort: descending ? `-${field}` : field, page: 1 });
    }
    async onPreviousPage() {
        await this.go({ page: Math.max(1, this.at.page - 1) });
    }
    async onNextPage() {
        await this.go({ page: Math.min(this.scope.pageCount, this.at.page + 1) });
    }
    async onGoToPage(page) {
        await this.go({ page });
    }
    /** O nome da empresa abre Colaboradores com ela no filtro - o mesmo link do painel antigo. */
    async onOpenCollaborators(companyId) {
        const target = CollaboratorsKeys.of(this.app);
        target.companyId = companyId;
        target.page = 1;
        await target.flip();
    }
    /** O scorecard como a tela o mostra - o recorte e a ordem -, com todas as paginas. */
    async onExportScorecard() {
        const answer = this.companies;
        if (!answer) {
            return;
        }
        const days = answer.windowDays;
        const fraction = (value) => (value === null ? '' : csvDecimal(value * 100, 1));
        FileSaver.INSTANCE.save({
            name: `icp-scorecard-${answer.endDate}-${days}d.csv`,
            content: csvBlob([
                'Empresa',
                'Score',
                'Faixa',
                'Segmento',
                'Porte',
                'Fonte do porte',
                'Colaboradores',
                'Já sincronizaram',
                `Ativos em ${days} dias`,
                'Ativação (%)',
                'Engajamento (%)',
                'Retenção (%)',
                'Treino (%)',
                'Treinos por ativo',
                'Meses de cliente'
            ], this.visibleCompanies().map((company) => [
                company.name,
                company.score === null ? '' : csvDecimal(company.score, 1),
                BAND_LABELS[company.band] ?? company.bandLabel,
                company.segmentLabel ?? '',
                company.sizeBandLabel ?? '',
                company.sizeSource ?? '',
                company.collaborators,
                company.everSynced,
                company.activeInWindow,
                fraction(company.activation),
                fraction(company.engagement),
                fraction(company.retention),
                fraction(company.training),
                company.workoutsPerActive === null ? '' : csvDecimal(company.workoutsPerActive, 2),
                company.monthsAsClient ?? ''
            ]))
        });
    }
}
//# sourceMappingURL=icp.presenter.js.map